import { useLocation } from "@tanstack/react-router"
import { useCallback } from "react"
import type { FileRoutesByFullPath } from "routeTree.gen"

const headerShownRoutes = new Set<keyof FileRoutesByFullPath>([
  "/app/tablet/warehouse-receiving/incoming",
  "/app/tablet/warehouse-receiving/soft-received",
  "/app/tablet/warehouse-receiving/checked-in",
  "/app/tablet/warehouse-receiving/stored",
  "/app/tablet/sales/",
])

export enum BasicPathType {
  WAREHOUSE_RECEIVING = "WAREHOUSE_RECEIVING",
}

const PathTypeToDesktopRouteMap = {
  [BasicPathType.WAREHOUSE_RECEIVING]: "/app/warehouse-receiving",
} as const

const PathTypeToTabletRouteMap = {
  [BasicPathType.WAREHOUSE_RECEIVING]: "/app/tablet/warehouse-receiving",
} as const

interface UseAppViewReturn {
  isTabletView: boolean
  shouldShowSideNav: boolean
  shouldShowAppHeader: boolean
  getBasicPathFor: (
    pathType: BasicPathType
  ) =>
    | (typeof PathTypeToDesktopRouteMap)[keyof typeof PathTypeToDesktopRouteMap]
    | (typeof PathTypeToTabletRouteMap)[keyof typeof PathTypeToTabletRouteMap]
}

export const useAppView = (): UseAppViewReturn => {
  const pathname = useLocation({
    select: (location) => location.pathname as keyof FileRoutesByFullPath,
  })
  const isTabletView = pathname.includes("/app/tablet")

  const shouldShowSideNav = !isTabletView
  const shouldShowAppHeader = !isTabletView || headerShownRoutes.has(pathname)

  const getBasicPathFor = useCallback(
    (pathType: BasicPathType) => {
      if (!PathTypeToTabletRouteMap[pathType] || !PathTypeToDesktopRouteMap[pathType]) {
        throw new Error(`Path type ${pathType} not found`)
      }
      return isTabletView ? PathTypeToTabletRouteMap[pathType] : PathTypeToDesktopRouteMap[pathType]
    },
    [isTabletView]
  )

  return { isTabletView, shouldShowSideNav, shouldShowAppHeader, getBasicPathFor }
}
